import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import * as catalog from "../../eaiws/catalog";
import { CameraMode } from "../../core/view";
import { ListenableEvent, Log } from "../../utils";
import { forEachParallel } from "../../utils/async";
import { ERR_FAILED, ERR_INVALID_ARGUMENT, ERR_INVALID_STATE, ERR_NOT_AVAILABLE, newError, rejectError } from "../../utils/error";
import { isNullOrEmpty } from "../../utils/string";
import { quaternionFromAxes, rotateVector3, VECTOR3_Z } from "../../core/utils/MathUtils";
import { degToRad, EPSILON, radToDeg } from "../../utils/math";
import { ArticleItem } from "../ArticleItem";
import { ItemUpdateFlags } from "../BaseItem";
import { ViewArticleItem } from "../ViewArticleItem";
import { SetArticleItem } from "../SetArticleItem";
import { MultiArticlePropertyProvider } from "./MultiArticlePropertyProvider";
export class ArticleConfiguration {
    get started() {
        return (this.mStarted);
    }
    get isPreConfiguration() {
        return (this.mStarted && this.mIsPreConfiguration);
    }
    get articleManager() {
        return (this.mArticleManager);
    }
    constructor(pBasket) {
        this.onUpdateSessionLanguage = () => {
            if (this.mArticleManager != null) {
                this.mArticleManager.resetCachedArticleInfos(true);
            }
        };
        this.onClear = () => {
            if (this.mStarted) {
                this.reset();
            }
        };
        this.onArticleChanged = (pData) => {
            if (pData != null && pData.article != null && pData.article.basketId != null) {
                const tMainArticle = pData.article.getMainArticle();
                if (tMainArticle != null)
                    this.mChangedMainElements.add(tMainArticle);
            }
        };
        this.onArticleAdded = (pArticle) => {
            if (pArticle != null && pArticle.basketId != null) {
                const tMainArticle = pArticle.getMainArticle();
                this.mChangedMainElements.add(tMainArticle);
                if (pArticle !== tMainArticle) {
                    //sub article was added, update parent of main article (maybe sub article is a sibling)
                    let tParent = null;
                    const tMainArticleId = tMainArticle.basketId;
                    if (tMainArticleId != null) {
                        const tMainItem = this.mBasket.getItemById(tMainArticleId);
                        if (tMainItem != null)
                            tParent = tMainItem.parent;
                    }
                    if (tParent == null)
                        tParent = this.mMainParent; //fallback to main parent
                    this.mParentItemsToUpdate.add(tParent.id);
                }
                else {
                    //main article was added, update main parent
                    this.mParentItemsToUpdate.add(this.mMainParent.id);
                }
            }
        };
        this.onArticleRemoved = (pArticleElement) => {
            if (pArticleElement != null && pArticleElement.basketId != null) {
                const tMainArticleElement = pArticleElement.getMainArticle();
                this.mChangedMainElements.add(tMainArticleElement);
                if (pArticleElement !== tMainArticleElement) {
                    //sub article was removed, update parent of subarticle (if it is not a child of the main article)
                    let tParent = null;
                    const tSubArticle = this.mBasket.getItemById(pArticleElement.basketId);
                    if (tSubArticle != null && tSubArticle instanceof ArticleItem) {
                        tParent = tSubArticle.parent;
                        if (tParent == null)
                            tParent = this.mMainParent; //fallback to main parent
                        //Check if the parent is a sub article of the main article or the main article itself,
                        //if yes the parent does not need to be update separately.
                        //This would also lead to unknown item id errors in some cases for nested sub articles.
                        const tMainArticle = tSubArticle.getMainArticle();
                        if (tParent === tMainArticle || (tParent instanceof ArticleItem && tParent.getMainArticle() === tMainArticle)) {
                            tParent = null;
                        }
                    }
                    if (tParent != null)
                        this.mParentItemsToUpdate.add(tParent.id);
                }
                else {
                    //main item was removed, update main parent
                    //TODO: do we need to update all sub article parents here?
                    this.mParentItemsToUpdate.add(this.mMainParent.id);
                }
            }
        };
        this.onArticleInstanceRestored = (pArticle) => {
            if (pArticle != null && pArticle.basketId != null) {
                this.mRestoredInstances.add(pArticle.basketId);
                this.mChangedMainElements.add(pArticle);
                this.mParentItemsToUpdate.add(this.mMainParent.id);
            }
        };
        this.onModelModified = () => {
            if (this.mLiveBasketUpdate) {
                void this.synchronizeBasket(false);
            }
        };
        this.eventBasketChanged = new ListenableEvent(this);
        this.mBasket = pBasket;
        this.mParentItemsToUpdate = new Set();
        this.mChangedMainElements = new Set();
        this.mRestoredInstances = new Set();
        this.mAssignedCameras = new Map();
        this.mBasket.eventUpdateSessionLanguage.addListener(this.onUpdateSessionLanguage);
        this.mBasket.eventClear.addListener(this.onClear);
        this.reset(false);
    }
    async start(pArticleManager, pArticle, pOptions) {
        if (pArticleManager == null || pArticle == null)
            throw new Error(ERR_INVALID_ARGUMENT);
        if (this.started)
            throw newError(ERR_INVALID_STATE, "configuration already started");
        try {
            this.mArticleManager = pArticleManager;
            this.mApp = pArticleManager.app;
            if (pArticle instanceof ViewArticleItem)
                return (await this.startForArticle(pArticle._getConfigArticleItem(true), pOptions));
            if (pArticle instanceof ArticleItem)
                return (await this.startForArticle(pArticle, pOptions));
            else
                return (await this.startPreConfiguration(pArticle, pOptions));
        }
        catch (pError) {
            this.reset();
            throw pError;
        }
    }
    /**
    * Start configuration of multiple articles.
    */
    startMultiConfiguration(pArticles) {
        if (this.started)
            throw newError(ERR_INVALID_STATE, "configuration already started");
        this.mMultiPropProvider = MultiArticlePropertyProvider._Create(this.mBasket, pArticles);
        this.mStarted = true;
        return (Promise.resolve(this.mMultiPropProvider));
    }
    /**
     * Assign camera view to article which is used to generate the image for the ArticleItem.
     * If pView is null the default camera setup will be used (can be used to reset the article camera).
     */
    async assignArticleImageCamera(pArticle, pView, pOptions) {
        if ((pOptions === null || pOptions === void 0 ? void 0 : pOptions.wholeComposite) === true) {
            const tMain = pArticle.getMainArticle();
            const tSubArticles = tMain.getSubArticles(false);
            this.addAssignedCamera(tMain, pView);
            for (const tSub of tSubArticles) {
                this.addAssignedCamera(tSub, pView);
            }
        }
        else {
            this.addAssignedCamera(pArticle, pView);
        }
        if (this.mLiveBasketUpdate) {
            const tImagesToUpdate = new Array();
            await this.synchronizeBasket(false, (pArticle) => tImagesToUpdate.push(pArticle));
            if ((pOptions === null || pOptions === void 0 ? void 0 : pOptions.updateArticleImages) === true) {
                const tOnProgress = pOptions.onUpdateImagesProgress;
                let tAborted = false;
                let tNumFinished = 0;
                await forEachParallel(tImagesToUpdate, async (pIndex, pArticle) => {
                    if (tAborted)
                        return;
                    await pArticle.getImageUrl();
                    if (tOnProgress != null) {
                        tAborted = !tOnProgress(++tNumFinished, tImagesToUpdate.length);
                    }
                }, ArticleConfiguration.MAX_CONCURRENT_IMAGE_UPDATES);
            }
        }
    }
    /**
     * Returns the currently assigned camera setup for the given article.
     */
    getArticleImageCamera(pArticle) {
        const tSetup = this.mAssignedCameras.get(pArticle);
        if (tSetup != null)
            return (tSetup);
        if (pArticle.basketId != null) {
            const tItem = this.mBasket.getItemById(pArticle.basketId);
            if (tItem != null && tItem instanceof ArticleItem)
                return (tItem.getCameraSetup());
        }
        return (null);
    }
    /**
     * Resets the camera of the given view to the initial camera setup for the current article.
     */
    resetView(pView, pZoomToFitOptions) {
        this.setupCamera({ view: pView, zoomToFitOptions: pZoomToFitOptions });
    }
    /**
     * Finish configuration.
     */
    async finish() {
        if (!this.started)
            return (rejectError(ERR_INVALID_STATE, "configuration not started"));
        if (this.mMultiPropProvider != null) {
            this.mMultiPropProvider.setProviders([]);
            this.mMultiPropProvider = null;
            this.mStarted = false;
        }
        else {
            if (this.mIsPreConfiguration)
                await this.setPreConfigurationItemId(null);
            return (this.synchronizeBasket(true));
        }
    }
    /**
     * Aborts the configuration process. Only supported for new articles.
     */
    async abort() {
        if (!this.started)
            throw newError(ERR_INVALID_STATE, "configuration not started");
        if (!this.mIsPreConfiguration)
            throw newError(ERR_NOT_AVAILABLE, "only available for new articles");
        try {
            if (this.mArticleManager != null) {
                const tMainArticles = this.mArticleManager.getAllMainArticles();
                for (const tMainArticle of tMainArticles) {
                    this.mApp.model.removeElement(tMainArticle);
                }
                await this.setPreConfigurationItemId(null);
                await this.mArticleManager.synchronizeSession(false);
            }
        }
        finally {
            this.reset();
        }
    }
    /**
     * If an OBK was saved during pre configuration of an catalog item, the session still
     * contains this item. This function should be called before calling `Basket.loadFromSession()`
     * to delete the item from the session prior to loading.
     * A function to restore the configuration state of this item be returend in this case.
     * If this function is not called no special handling for this item will be performed.
     * It will be handled as a regular basket item.
     */
    async loadPreConfigurationFromSession() {
        const tPreConfigItemId = await this.getPreConfigurationItemId();
        if (isNullOrEmpty(tPreConfigItemId))
            return (null);
        //reset id
        await this.setPreConfigurationItemId(null);
        try {
            const tIdArray = [tPreConfigItemId];
            //generate obx and delete item afterwards
            const tObxUrl = await this.mBasket.session.basket.copy(tIdArray);
            await this.mBasket.session.basket.deleteItems(tIdArray);
            return ((pArticleManager, pOptions) => {
                return (this.restorePreConfiguration(pArticleManager, tObxUrl, pOptions));
            });
        }
        catch (pError) {
            Log.error("Failed to prepare restoration of pre configuration.", pError);
        }
        return (null);
    }
    reset(pClear = true) {
        if (pClear && this.mApp != null && this.mArticleManager != null) {
            this.deregisterEvents();
            this.mApp.document.clear();
            this.mArticleManager.fatherItemId = this.mOldFatherItemId;
            this.mArticleManager.setPricingProcedure(this.mOldPricingProcedure);
        }
        this.mStarted = false;
        this.mLiveBasketUpdate = false;
        this.mArticleManager = null;
        this.mApp = undefined;
        this.mMultiPropProvider = null;
        this.mIsPreConfiguration = false;
        this.mMainParent = undefined;
        this.mMainItem = null;
        this.mPreConfigMainItemId = null;
        this.mOldFatherItemId = null;
        this.mOldPricingProcedure = null;
        this.mOldIncludeCalculationErrorsInInconsistencies = true;
        this.clearTrackedChanges();
    }
    clearTrackedChanges() {
        this.mParentItemsToUpdate.clear();
        this.mChangedMainElements.clear();
        this.mRestoredInstances.clear();
        this.mAssignedCameras.clear();
    }
    registerEvents() {
        if (this.mArticleManager != null) {
            this.mArticleManager.eventArticleChanged.addListener(this.onArticleChanged);
            this.mArticleManager.eventArticleAdded.addListener(this.onArticleAdded);
            this.mArticleManager.eventArticleRemoved.addListener(this.onArticleRemoved);
            this.mArticleManager.eventArticleInstanceRestored.addListener(this.onArticleInstanceRestored);
        }
        this.mApp.model.eventModified.addListener(this.onModelModified);
    }
    deregisterEvents() {
        if (this.mArticleManager != null) {
            this.mArticleManager.eventArticleChanged.removeListener(this.onArticleChanged);
            this.mArticleManager.eventArticleAdded.removeListener(this.onArticleAdded);
            this.mArticleManager.eventArticleRemoved.removeListener(this.onArticleRemoved);
            this.mArticleManager.eventArticleInstanceRestored.removeListener(this.onArticleInstanceRestored);
        }
        this.mApp.model.eventModified.removeListener(this.onModelModified);
    }
    async startForArticle(pArticle, pOptions) {
        if (this.mArticleManager == null)
            throw newError(ERR_INVALID_STATE, "mArticleManager == null");
        if (pArticle instanceof SetArticleItem)
            throw newError(ERR_INVALID_ARGUMENT, "Set articles are not supported.");
        this.mStarted = true;
        this.mLiveBasketUpdate = pOptions != null && pOptions.liveBasketUpdate === true;
        this.mIsPreConfiguration = false;
        this.mOldFatherItemId = this.mArticleManager.fatherItemId;
        this.mOldPricingProcedure = this.mArticleManager.pricingProcedure;
        this.mOldIncludeCalculationErrorsInInconsistencies = this.mArticleManager.includeCalculationErrorsInInconsistencies;
        this.mArticleManager.includeCalculationErrorsInInconsistencies = this.mBasket.includeCalculationErrorsInInconsistencies;
        this.mArticleManager.setPricingProcedure(this.mBasket.calculation.pricingProcedure);
        const tMainArticleItem = pArticle.getMainArticle();
        //collect articles
        const tMainArticleIds = new Array();
        const tArticleBasketItems = new Array();
        const tGfjUrls = new Map();
        this.mMainItem = tMainArticleItem;
        this.mMainParent = tMainArticleItem.parent != null ? tMainArticleItem.parent : this.mBasket.root;
        this.mArticleManager.fatherItemId = this.mMainParent.id;
        tMainArticleIds.push(tMainArticleItem.id);
        const tConfData = tMainArticleItem._getCachedConfigData();
        if (tConfData != null && tConfData.gfjUrl != null)
            tGfjUrls.set(tMainArticleItem.id, tConfData.gfjUrl);
        this.collectArticleBasketItems(tMainArticleItem, tArticleBasketItems);
        //create articles
        const tMainElements = await this.mArticleManager.createArticlesById(tMainArticleIds, false, tArticleBasketItems, tGfjUrls);
        //add to model and fill cache
        let tSelectionElement = null;
        for (const tMainElement of tMainElements) {
            this.mApp.model.addElement(tMainElement);
            this.fillMainElementCache(tMainElement);
            if (tMainElement.basketId === tMainArticleItem.id)
                tSelectionElement = tMainElement;
        }
        //set selection
        if (tSelectionElement != null) {
            this.mApp.model.setSelection([tSelectionElement]);
            if (pArticle.isSubArticle()) {
                const tSubElement = tSelectionElement.getSubArticleById(pArticle.id);
                this.mApp.model.setSubElementSelection(tSelectionElement, tSubElement);
            }
        }
        else {
            this.mApp.model.setSelection(null);
        }
        //init camera
        this.setupCamera(pOptions);
        this.registerEvents();
        return (tMainElements);
    }
    collectArticleBasketItems(pMainItem, pItems) {
        const tSubArticles = pMainItem.getSubArticles(true);
        pItems.push(pMainItem.item);
        for (const tSub of tSubArticles)
            pItems.push(tSub.item);
    }
    async restorePreConfiguration(pArticleManager, pObxUrl, pOptions) {
        if (this.started)
            throw newError(ERR_INVALID_STATE, "configuration already started");
        try {
            this.mArticleManager = pArticleManager;
            this.mApp = pArticleManager.app;
            //paste obx
            const tIds = await this.mBasket.session.basket.paste(null, null, pObxUrl);
            if (tIds == null || tIds.length < 1)
                throw newError(ERR_FAILED, "Failed to restore pre configuration.");
            return (await this.startPreConfiguration(tIds[0], pOptions));
        }
        catch (pError) {
            this.reset();
            throw pError;
        }
    }
    async startPreConfiguration(pArticle, pOptions) {
        if (this.mArticleManager == null)
            throw newError(ERR_INVALID_STATE, "mArticleManager == null");
        this.mStarted = true;
        this.mIsPreConfiguration = true;
        this.mMainParent = this.mBasket.root;
        this.mOldFatherItemId = this.mArticleManager.fatherItemId;
        this.mOldPricingProcedure = this.mArticleManager.pricingProcedure;
        this.mArticleManager.includeCalculationErrorsInInconsistencies = this.mOldIncludeCalculationErrorsInInconsistencies;
        this.mArticleManager.setPricingProcedure(this.mBasket.calculation.pricingProcedure);
        this.mMainItem = null;
        this.mArticleManager.fatherItemId = null;
        let tElement;
        if (pArticle instanceof catalog.ArticleCatalogItem)
            tElement = await this.mArticleManager.insertArticle(pArticle);
        else
            tElement = await this.mArticleManager.createArticleById(pArticle);
        //mark as pre configuration item
        await this.setPreConfigurationItemId(tElement.basketId);
        this.mPreConfigMainItemId = tElement.basketId;
        this.registerEvents();
        this.mApp.model.addElement(tElement);
        this.mApp.model.setSelection([tElement]);
        //init camera
        this.setupCamera(pOptions);
        return ([tElement]);
    }
    setPreConfigurationItemId(pId) {
        let tKeyValue = ArticleConfiguration.APP_DATA_PRECONFIGURATION_ITEM + "=";
        if (pId != null)
            tKeyValue += pId;
        return (this.mBasket.session.basket.setBasketAppData(ArticleConfiguration.APP_KEY, [tKeyValue]));
    }
    getPreConfigurationItemId() {
        const tPromise = this.mBasket.session.basket.getBasketAppData(ArticleConfiguration.APP_KEY, [ArticleConfiguration.APP_DATA_PRECONFIGURATION_ITEM]).then((pValues) => {
            if (pValues == null || pValues.length !== 1)
                return (null);
            return (pValues[0]);
        });
        return (tPromise);
    }
    setupCamera(pOptions) {
        if (pOptions == null || pOptions.view == null)
            return;
        //use article image camera setup from eaiws
        let tCamSetup;
        if (this.mMainItem != null) {
            //get currently assigned setup (resetView)
            if (this.mArticleManager != null) {
                const tElement = this.mArticleManager.getArticleById(this.mMainItem.id);
                if (tElement != null)
                    tCamSetup = this.mAssignedCameras.get(tElement);
            }
            //get setup from item
            if (tCamSetup == null)
                tCamSetup = this.mMainItem.getCameraSetup();
        }
        else {
            //pre configuration
            if (this.mIsPreConfiguration && this.mPreConfigMainItemId != null) {
                if (this.mArticleManager != null) {
                    const tElement = this.mArticleManager.getArticleById(this.mPreConfigMainItemId);
                    if (tElement != null)
                        tCamSetup = this.mAssignedCameras.get(tElement);
                }
            }
        }
        if (tCamSetup == null)
            tCamSetup = ArticleConfiguration.DEFAULT_CAMERA_SETUP;
        const tQuat = Quaternion.RotationYawPitchRoll(degToRad(-tCamSetup.alpha), degToRad(tCamSetup.beta), 0.0);
        try {
            pOptions.view.cameraControl.beginChange();
            pOptions.view.cameraControl.cameraMode = tCamSetup.mode === "Perspective" ?
                CameraMode.Perspective :
                CameraMode.Orthographic;
            pOptions.view.cameraControl.setDirection(rotateVector3(VECTOR3_Z, tQuat));
            pOptions.view.cameraControl.setFov(degToRad(tCamSetup.fov));
            if (pOptions.zoomToFitOptions != null)
                pOptions.view.cameraControl.zoomToFitElements(null, pOptions.zoomToFitOptions);
        }
        finally {
            pOptions.view.cameraControl.endChange();
        }
    }
    addAssignedCamera(pArticle, pView) {
        if (pView == null) {
            this.mAssignedCameras.set(pArticle, null);
            return;
        }
        const tX = pView.cameraControl.right.clone();
        const tZ = pView.cameraControl.direction.clone();
        const tY = Vector3.Cross(tZ, tX);
        Vector3.TransformNormalToRef(tX, pArticle.worldTransform, tX);
        Vector3.TransformNormalToRef(tY, pArticle.worldTransform, tY);
        Vector3.TransformNormalToRef(tZ, pArticle.worldTransform, tZ);
        const tQuat = quaternionFromAxes(tX, tY, tZ);
        const tEuler = tQuat.toEulerAngles();
        const tCamSetup = {};
        tCamSetup.mode = pView.cameraControl.cameraMode === CameraMode.Perspective ? "Perspective" : "Orthographic";
        tCamSetup.alpha = radToDeg(-tEuler.y);
        tCamSetup.beta = radToDeg(tEuler.x);
        tCamSetup.fov = radToDeg(pView.camera.fov);
        //validation to prevent exception on eaiws
        if (tCamSetup.mode === "Orthographic") {
            if (tCamSetup.beta <= -90) {
                tCamSetup.alpha = 0;
                tCamSetup.beta = -90;
            }
            else if (tCamSetup.beta >= 90) {
                tCamSetup.alpha = 0;
                tCamSetup.beta = 90;
            }
        }
        else {
            if (tCamSetup.beta <= -90) {
                tCamSetup.beta = -90 + EPSILON;
            }
            else if (tCamSetup.beta >= 90) {
                tCamSetup.beta = 90 - EPSILON;
            }
        }
        this.mAssignedCameras.set(pArticle, tCamSetup);
    }
    fillMainElementCache(pMainElement) {
        if (pMainElement.basketId == null)
            return;
        const tBasketItem = this.mBasket.getItemById(pMainElement.basketId);
        if (tBasketItem == null || !(tBasketItem instanceof ArticleItem))
            return;
        const tAllArticles = tBasketItem.getSubArticles(true);
        tAllArticles.push(tBasketItem);
        const tItemProperties = new Array();
        const tCalculations = new Map();
        const tConfigData = new Map();
        for (const tArticle of tAllArticles) {
            const tProps = tArticle.getCachedItemProperties();
            if (tProps != null)
                tItemProperties.push(tProps);
            const tCalc = tArticle.getCachedCalculation();
            if (tCalc != null)
                tCalculations.set(tBasketItem.id, tCalc);
            const tConfData = tArticle._getCachedConfigData();
            if (tConfData != null)
                tConfigData.set(tBasketItem.id, tConfData);
        }
        pMainElement.fillItemPropertiesCache(tItemProperties);
        pMainElement.fillCalculationCache(tCalculations);
        pMainElement.fillConfigDataCache(tConfigData);
    }
    fillBasketCache() {
        if (this.mChangedMainElements.size === 0)
            return (null);
        const tMainItemIds = new Array();
        this.mChangedMainElements.forEach((pMainArticle) => {
            if (pMainArticle.basketId != null)
                tMainItemIds.push(pMainArticle.basketId);
            this.addArticleDataToCache(pMainArticle);
            const tChildren = pMainArticle.getSubArticles(false);
            for (const tChild of tChildren)
                this.addArticleDataToCache(tChild);
        });
        return (tMainItemIds);
    }
    fillBasketConfigDataCache() {
        if (this.mArticleManager == null)
            throw newError(ERR_INVALID_STATE, "mArticleManager == null");
        const tMainArticles = this.mArticleManager.getAllMainArticles();
        for (const tMain of tMainArticles) {
            this.addConfigDataToCache(tMain);
            const tChildren = tMain.getSubArticles(false);
            for (const tChild of tChildren)
                this.addConfigDataToCache(tChild);
        }
    }
    addArticleDataToCache(pArticle) {
        if (pArticle == null || pArticle.basketId == null)
            return;
        //cache item
        const tItem = pArticle.getCachedBasketItem();
        if (tItem != null)
            this.mBasket._addEaiwsItemToCache(tItem);
        //cache item properties
        const tProps = pArticle.getCachedItemProperties();
        if (tProps != null)
            this.mBasket._addItemPropsToCache(tProps);
        //cache calculation
        const tCalc = pArticle.getCachedCalculation();
        if (tCalc != null)
            this.mBasket._addCalculationInfoToCache(pArticle.basketId, tCalc);
    }
    addConfigDataToCache(pArticle) {
        if (pArticle == null || pArticle.basketId == null)
            return;
        const tItem = this.mBasket.getItemById(pArticle.basketId);
        if (tItem instanceof ArticleItem)
            tItem._setCachedConfigData(pArticle.getCachedConfigData());
    }
    async synchronizeBasket(pFinalize, pNeedImageUpdateCallback) {
        if (this.mArticleManager == null)
            throw newError(ERR_INVALID_STATE, "mArticleManager == null");
        let tTriggerChangedEvent = false;
        try {
            this.mBasket._beginItemUpdate();
            this.mBasket._beginCaching();
            if (!pFinalize && this.mApp.appCallbacks.beginAsyncOperation != null)
                this.mApp.appCallbacks.beginAsyncOperation();
            //sync session to create copied articles on EAIWS side
            await this.mArticleManager.synchronizeSession(false);
            //prepare cache
            const tMainItemsToUpdate = this.fillBasketCache();
            if (tMainItemsToUpdate != null) {
                //cache all main items including sub articles
                await this.mBasket._fetchEaiwsItems(tMainItemsToUpdate, false, true);
            }
            //update parent items (maybe items where added or removed)
            if (this.mParentItemsToUpdate.size > 0) {
                const tParentItemsToUpdate = Array.from(this.mParentItemsToUpdate);
                //cache all parent items first
                await this.mBasket._fetchEaiwsItems(tParentItemsToUpdate, false, false);
                for (const tId of tParentItemsToUpdate) {
                    const tParent = this.mBasket.getItemById(tId);
                    if (tParent != null) {
                        await tParent.updateItem(ItemUpdateFlags.Item);
                        tParent.invalidateGroupCalculations();
                    }
                }
                tTriggerChangedEvent = true;
            }
            //update changed main articles
            if (!this.mIsPreConfiguration && tMainItemsToUpdate != null) {
                for (const tId of tMainItemsToUpdate) {
                    const tItem = this.mBasket.getItemById(tId);
                    if (tItem != null && tItem instanceof ArticleItem) {
                        await tItem.updateWholeComposite(ItemUpdateFlags.All & ~ItemUpdateFlags.UserFields);
                    }
                }
                tTriggerChangedEvent = true;
            }
            //assign article image camera
            const tImageUpdatePromises = new Array();
            this.mAssignedCameras.forEach((pCamSetup, pArticleElement) => {
                if (pArticleElement.basketId == null)
                    return;
                const tItem = this.mBasket.getItemById(pArticleElement.basketId);
                if (tItem != null && tItem instanceof ArticleItem) {
                    if (tItem.getCameraSetup() !== pCamSetup) {
                        tImageUpdatePromises.push(tItem.setCameraSetup(pCamSetup, true));
                        if (pNeedImageUpdateCallback != null) {
                            pNeedImageUpdateCallback(tItem);
                        }
                        tTriggerChangedEvent = true;
                    }
                }
            });
            await Promise.all(tImageUpdatePromises);
            //cache config data
            if (pFinalize)
                this.fillBasketConfigDataCache();
        }
        finally {
            if (!pFinalize && this.mApp.appCallbacks.endAsyncOperation != null)
                this.mApp.appCallbacks.endAsyncOperation();
            if (pFinalize) {
                this.reset();
            }
            else
                this.clearTrackedChanges();
            this.mBasket._endCaching();
            this.mBasket._endItemUpdate();
            if (tTriggerChangedEvent) {
                this.eventBasketChanged.trigger();
                this.mBasket.eventModified.trigger(); //just to be sure, should not be necessary actually
            }
        }
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
ArticleConfiguration.APP_KEY = "W-CF";
ArticleConfiguration.APP_DATA_PRECONFIGURATION_ITEM = "basket/preConfigurationItem";
ArticleConfiguration.DEFAULT_CAMERA_SETUP = {
    alpha: -15,
    beta: 20,
    fov: 30,
    mode: "Perspective"
};
ArticleConfiguration.MAX_CONCURRENT_IMAGE_UPDATES = 5;
